/** The status vocabulary — every label and colour a state gets in the console,
 *  in one table (plan 1-3). Screens never spell a state out themselves: they
 *  call `statusOf(domain, key)` and render a StatusPill. */

export type Tone =
  | 'neutral'
  | 'review'
  | 'scheduled'
  | 'publishing'
  | 'published'
  | 'failed'
  | 'deadline'
  | 'web'

export interface StatusDef {
  label: string
  tone: Tone
  /** The dot blinks: something is under way right now. */
  pulse?: boolean
  /** Shown faded: the item is out of play. */
  dim?: boolean
}

/** Campaign drafts and desk posts share one set: a post is a post. */
const POST: Record<string, StatusDef> = {
  draft: { label: '초안', tone: 'neutral' },
  pending_review: { label: '검토 대기', tone: 'review' },
  approved: { label: '승인', tone: 'scheduled' },
  scheduled: { label: '예약', tone: 'scheduled' },
  publishing: { label: '게시 중', tone: 'publishing', pulse: true },
  published: { label: '게시됨', tone: 'published' },
  failed: { label: '실패', tone: 'failed' },
  rejected: { label: '반려', tone: 'neutral', dim: true },
  judge_rejected: { label: '심사 탈락', tone: 'neutral', dim: true },
}

const REQUEST: Record<string, StatusDef> = {
  new: { label: '대기', tone: 'review' },
  issued: { label: '발급됨', tone: 'scheduled' },
  sent: { label: '전송됨', tone: 'scheduled' },
  replied: { label: '완료', tone: 'published' },
  failed: { label: '실패', tone: 'failed' },
  duplicate: { label: '중복', tone: 'neutral' },
  ignored: { label: '제외', tone: 'neutral' },
}

const CODE: Record<string, StatusDef> = {
  available: { label: '남음', tone: 'published' },
  issued: { label: '나감', tone: 'neutral' },
  void: { label: '무효', tone: 'failed' },
}

const APP: Record<string, StatusDef> = {
  active: { label: '가동', tone: 'published' },
  paused: { label: '멈춤', tone: 'publishing' },
  archived: { label: '보관', tone: 'neutral' },
}

const ACCOUNT: Record<string, StatusDef> = {
  active: { label: '사용', tone: 'published' },
  disabled: { label: '중지', tone: 'neutral' },
}

const MARKER: Record<string, StatusDef> = {
  expiry: { label: '코드 만료', tone: 'deadline' },
  free_until: { label: '무료 종료', tone: 'web' },
  ended: { label: '나눔 종료', tone: 'neutral' },
}

/** Outreach: a maker moves through these by hand, one step at a time. */
const PROSPECT: Record<string, StatusDef> = {
  discovered: { label: '발견', tone: 'neutral' },
  qualified: { label: '적격', tone: 'review' },
  connection_ready: { label: '연결 요청 준비', tone: 'review' },
  connection_sent: { label: '연결 요청 보냄', tone: 'scheduled' },
  connected: { label: '연결됨', tone: 'scheduled' },
  praise_ready: { label: '칭찬 준비', tone: 'review' },
  praise_sent: { label: '칭찬 보냄', tone: 'scheduled' },
  replied: { label: '답장 옴', tone: 'published' },
  cta_ready: { label: '제안 준비', tone: 'review' },
  cta_sent: { label: '제안 보냄', tone: 'scheduled' },
  closed: { label: '종료', tone: 'published' },
  skipped: { label: '건너뜀', tone: 'neutral', dim: true },
  do_not_contact: { label: '연락 금지', tone: 'failed' },
  invalid_profile: { label: '프로필 무효', tone: 'failed', dim: true },
}

const STEP: Record<string, StatusDef> = {
  draft: { label: '초안', tone: 'review' },
  approved: { label: '승인', tone: 'scheduled' },
  sent: { label: '보냄', tone: 'published' },
  discarded: { label: '폐기', tone: 'neutral', dim: true },
}

/** Influencer proposals: one person moves through these, a human deciding each
 *  step but the last. */
const CREATOR: Record<string, StatusDef> = {
  found: { label: '찾음', tone: 'neutral' },
  skipped: { label: '건너뜀', tone: 'neutral', dim: true },
  ready: { label: '승인 대기', tone: 'review' },
  approved: { label: '승인', tone: 'scheduled' },
  sent: { label: '보냄', tone: 'scheduled' },
  replied: { label: '답장 옴', tone: 'published' },
  accepted: { label: '수락', tone: 'published' },
  declined: { label: '거절', tone: 'neutral' },
  do_not_contact: { label: '연락 금지', tone: 'failed' },
}

/** The one pitch message of a proposal. */
const CREATOR_MESSAGE: Record<string, StatusDef> = {
  draft: { label: '초안', tone: 'review' },
  approved: { label: '승인', tone: 'scheduled' },
  sending: { label: '보내는 중', tone: 'publishing', pulse: true },
  sent: { label: '보냄', tone: 'published' },
  failed: { label: '실패', tone: 'failed' },
  discarded: { label: '폐기', tone: 'neutral', dim: true },
}

/** One DM outreach (발송). */
const OUTREACH: Record<string, StatusDef> = {
  draft: { label: '준비 중', tone: 'review' },
  active: { label: '진행 중', tone: 'published' },
  paused: { label: '멈춤', tone: 'publishing' },
  done: { label: '끝남', tone: 'neutral', dim: true },
}

/** A person's place in one pool. */
const POOL_MEMBER: Record<string, StatusDef> = {
  in: { label: '풀에 있음', tone: 'published' },
  removed: { label: '뺌', tone: 'neutral', dim: true },
}

export const STATUS = {
  post: POST,
  request: REQUEST,
  code: CODE,
  app: APP,
  account: ACCOUNT,
  marker: MARKER,
  prospect: PROSPECT,
  step: STEP,
  creator: CREATOR,
  creatorMessage: CREATOR_MESSAGE,
  outreach: OUTREACH,
  poolMember: POOL_MEMBER,
} as const

export type StatusDomain = keyof typeof STATUS

/** The label and tone for a state. An approved draft with no slot yet reads
 *  "슬롯 대기"; an unknown key comes back as itself, neutral, so a new server
 *  state shows up instead of vanishing. */
export function statusOf(
  domain: StatusDomain,
  key: string,
  opts: { scheduledAt?: string | null } = {},
): StatusDef {
  if (domain === 'post' && key === 'approved' && !opts.scheduledAt) {
    return { label: '슬롯 대기', tone: 'scheduled' }
  }
  return STATUS[domain][key] ?? { label: key, tone: 'neutral' }
}

/** Class names per tone. Kept literal so Tailwind sees every one. */
export const TONE_CLASS: Record<Tone, { text: string; soft: string; dot: string; border: string }> = {
  neutral: {
    text: 'text-status-neutral',
    soft: 'bg-status-neutral-soft',
    dot: 'bg-status-neutral',
    border: 'border-status-neutral/30',
  },
  review: {
    text: 'text-status-review',
    soft: 'bg-status-review-soft',
    dot: 'bg-status-review',
    border: 'border-status-review/30',
  },
  scheduled: {
    text: 'text-status-scheduled',
    soft: 'bg-status-scheduled-soft',
    dot: 'bg-status-scheduled',
    border: 'border-status-scheduled/30',
  },
  publishing: {
    text: 'text-status-publishing',
    soft: 'bg-status-publishing-soft',
    dot: 'bg-status-publishing',
    border: 'border-status-publishing/30',
  },
  published: {
    text: 'text-status-published',
    soft: 'bg-status-published-soft',
    dot: 'bg-status-published',
    border: 'border-status-published/30',
  },
  failed: {
    text: 'text-status-failed',
    soft: 'bg-status-failed-soft',
    dot: 'bg-status-failed',
    border: 'border-status-failed/30',
  },
  deadline: {
    text: 'text-status-deadline',
    soft: 'bg-status-deadline-soft',
    dot: 'bg-status-deadline',
    border: 'border-status-deadline/30',
  },
  web: {
    text: 'text-status-web',
    soft: 'bg-status-web-soft',
    dot: 'bg-status-web',
    border: 'border-status-web/30',
  },
}
